// Thrown blasts on the board (qa-runs#1171, the table half; docs/MRCS/shadowrun-3.md §18 "For the table"): where a
// thrown or launched explosive lands, how far it scatters, the Power that reaches each creature by its distance, and
// the walls that stop it. Every number is the system's content — a rule page's `data` (`throw`, `scatter`, `blast`),
// handed in parsed by `parseBlastRules` — never a number written here; a system with no such page has no blasts.
//
// Pure: no dice are thrown here. The caller throws the system's success test (`Roll`) and hands this module the
// faces; the server is the only one that does, so a preview and the authority cannot disagree.
//
// Board geometry is in board pixels (the grid's pitch, `pxPerSquare`) and the system's distance unit (`perSquare` of
// it a square). Screen y grows downward, so a positive angle from `atan2` turns clockwise as the page looks — the
// direction the scatter diagram counts in. "North" is the board's up.
import { segmentsMeet, blocksMove } from '../grid/walls.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const num = (v, min, max) => (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : null);
const int = (v, min, max) => { const n = num(v, min, max); return n !== null && Number.isInteger(n) ? n : null; };
const rising = (v, n) => Array.isArray(v) && v.length >= 1 && v.length <= 8 && (n === undefined || v.length === n) && v.every((x, i) => num(x, 0, 100_000) !== null && (i === 0 || x > v[i - 1])) ? [...v] : null;
/** A rule page's `data` as the table runs it; null when it is not a well-formed blast page. */
export function parseBlastRules(data) {
    if (!isObj(data) || !isObj(data.throw) || !isObj(data.scatter) || !isObj(data.blast))
        return null;
    const t = data.throw;
    const s = data.scatter;
    const b = data.blast;
    const tnByBand = rising(t.tn_by_band);
    const bands = Array.isArray(t.bands) && t.bands.every((x) => typeof x === 'string' && KEY_RE.test(x)) ? [...t.bands] : null;
    if (!tnByBand || !bands || bands.length !== tnByBand.length || !isObj(t.types))
        return null;
    const types = {};
    for (const [key, v] of Object.entries(t.types)) {
        if (!KEY_RE.test(key) || !isObj(v) || !isObj(v.range) || !isObj(v.scatter))
            return null;
        const sc = v.scatter;
        const scatter = { dice: int(sc.dice, 0, 20), sides: int(sc.sides, 2, 100), perSuccess: num(sc.metres_per_success, 0, 1000) };
        if (scatter.dice === null || scatter.sides === null || scatter.perSuccess === null)
            return null;
        let range = null;
        const metres = rising(v.range.metres, tnByBand.length);
        if (metres)
            range = { metres, ...(num(v.range.min, 0, 100_000) !== null ? { min: v.range.min } : {}) };
        else {
            const perKey = Object.keys(v.range).find((k) => /^per_[a-z][a-z0-9_]*$/.test(k));
            const multiples = perKey ? rising(v.range[perKey], tnByBand.length) : null;
            if (perKey && multiples)
                range = { per: perKey.slice(4), multiples };
        }
        if (!range)
            return null;
        const arms = num(v.arms_after_metres, 0, 1000);
        types[key] = { key, range, scatter: scatter, ...(arms !== null ? { armsAfter: arms } : {}) };
    }
    const dir = isObj(s.direction) ? s.direction : null;
    const degrees = {};
    if (!dir || !isObj(dir.degrees_clockwise))
        return null;
    for (const [face, deg] of Object.entries(dir.degrees_clockwise)) {
        const f = Number(face);
        const d = num(deg, 0, 360);
        if (!Number.isInteger(f) || d === null)
            return null;
        degrees[f] = d;
    }
    const dDice = int(dir.dice, 1, 4);
    const dSides = int(dir.sides, 2, 100);
    if (dDice === null || dSides === null)
        return null;
    if (!isObj(b.kinds))
        return null;
    const kinds = {};
    for (const [key, v] of Object.entries(b.kinds)) {
        if (!KEY_RE.test(key) || !isObj(v))
            return null;
        const power = int(v.power, 1, 1000);
        const perMetre = num(v.power_per_metre, 0.01, 1000);
        if (power === null || perMetre === null || typeof v.level !== 'string' || !KEY_RE.test(v.level))
            return null;
        kinds[key] = { key, power, level: v.level, stun: v.stun === true, perMetre };
    }
    const resist = isObj(b.resist) ? b.resist : {};
    return {
        tnByBand, bands, types, direction: { dice: dDice, sides: dSides, degrees }, kinds,
        itemAttribute: typeof b.item_attribute === 'string' && KEY_RE.test(b.item_attribute) ? b.item_attribute : null,
        stageEvery: int(resist.stage_every, 1, 20) ?? 1,
    };
}
/** The band a throw of `distance` falls in, and its TN; null beyond the last band or inside the minimum. */
export function throwBand(r, type, distance, attr) {
    const t = r.types[type];
    if (!t)
        return null;
    let tops;
    if ('metres' in t.range) {
        if (t.range.min !== undefined && distance < t.range.min)
            return null;
        tops = t.range.metres;
    }
    else {
        const a = attr(t.range.per);
        if (a === null || a <= 0)
            return null;
        tops = t.range.multiples.map((m) => m * a);
    }
    const d = Math.round(distance * 10) / 10;
    const i = tops.findIndex((top) => d <= top);
    return i < 0 ? null : { band: r.bands[i], tn: r.tnByBand[i], tops };
}
export function scatterOf(r, type, successes, directionFaces, distanceFaces) {
    const t = r.types[type];
    if (!t)
        return null;
    const face = directionFaces.reduce((a, b) => a + b, 0);
    const degrees = r.direction.degrees[face];
    if (degrees === undefined)
        return null;
    const rolled = distanceFaces.reduce((a, b) => a + b, 0);
    const metres = rolled - t.scatter.perSuccess * Math.max(0, successes);
    return metres <= 0 ? { face, degrees, rolled, metres: 0, lands: true } : { face, degrees, rolled, metres, lands: false };
}
/** Where the blast lands: `metres` from the aimed spot, turned `degrees` clockwise from the thrower-to-spot line. */
export function landingPoint(thrower, aimed, s, pxPerMetre) {
    if (s.metres <= 0)
        return { ...aimed };
    const base = aimed.x === thrower.x && aimed.y === thrower.y ? -Math.PI / 2 : Math.atan2(aimed.y - thrower.y, aimed.x - thrower.x);
    const a = base + (s.degrees * Math.PI) / 180;
    return { x: aimed.x + Math.cos(a) * s.metres * pxPerMetre, y: aimed.y + Math.sin(a) * s.metres * pxPerMetre };
}
const COMPASS = ['east', 'south-east', 'south', 'south-west', 'west', 'north-west', 'north', 'north-east'];
/** A screen direction in compass words, the board's up as north. */
export function compassOf(dx, dy) {
    const a = Math.atan2(dy, dx);
    const i = ((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8;
    return COMPASS[i];
}
/** The working's line: *Aimed at the door; scattered 3 m north-east.* `unit` is the system's distance unit's short form. */
export function scatterLine(aimedAt, thrower, aimed, landed, s, unit) {
    if (s.lands)
        return `Aimed at ${aimedAt}; landed true.`;
    return `Aimed at ${aimedAt}; scattered ${Math.round(s.metres * 10) / 10} ${unit} ${compassOf(landed.x - aimed.x, landed.y - aimed.y)}.`;
}
/** The Power left at `metres` from the blast point. Whole units count: 3.6 m away is 3 m out. */
export const powerAt = (power, perMetre, metres) => power - perMetre * Math.floor(metres + 1e-9);
/** The reach of a blast, in the unit: the farthest a creature can be and still take a Power of 1. */
export const blastReach = (power, perMetre) => Math.max(0, Math.floor((power - 1) / perMetre));
/**
 * Who a blast catches and the Power each takes: every creature whose centre is close enough for a Power of 1 or more,
 * and no wall that stops movement (a closed door included) lies between the blast point and it — walls carry no
 * rating yet, so each holds unless the DM marked it broken. Nearest first.
 */
export function blastCaught(a) {
    const holding = a.walls.filter((w) => blocksMove(w) && !(w.id && a.broken?.has(w.id)));
    const out = [];
    for (const t of a.targets) {
        if (t.dead)
            continue;
        const c = { x: t.x + t.w / 2, y: t.y + t.h / 2 };
        const metres = Math.hypot(c.x - a.point.x, c.y - a.point.y) / a.pxPerMetre;
        const power = powerAt(a.power, a.perMetre, metres);
        if (power < 1)
            continue;
        if (holding.some((w) => segmentsMeet(a.point, c, w.a, w.b)))
            continue;
        out.push({ id: t.id, metres: Math.round(metres * 10) / 10, power });
    }
    return out.sort((x, y) => x.metres - y.metres || x.id.localeCompare(y.id));
}
/**
 * One caught creature's resistance: dice (its Body and any pool, the caller's) against Power − armour, at least `least`;
 * the thrower's successes less its own stage the kind's level by one per `stageEvery`, up or down the system's levels.
 * Staged below the lowest is no damage; above the highest stays the highest.
 */
export function resistBlast(c, thrower, resist, base, levels, stageEvery) {
    const tn = Math.max(resist.least, c.power - resist.armour);
    const net = thrower - resist.successes;
    const stages = net >= 0 ? Math.floor(net / stageEvery) : -Math.floor(-net / stageEvery);
    const at = levels.findIndex((l) => l.key === base);
    const i = at < 0 ? -1 : Math.min(levels.length - 1, at + stages);
    const level = i >= 0 ? levels[i] : null;
    return { id: c.id, power: c.power, tn, dice: resist.dice, successes: resist.successes, net, level: level?.key ?? null, boxes: level?.boxes ?? 0 };
}
//# sourceMappingURL=blast.js.map